import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Moon, Sun, Instagram, Youtube, MessageCircle,
  History, LogOut, LayoutDashboard, Crown, Menu, X, Palette, BookOpen, Headphones,
  GemIcon, Lock, Newspaper, Book, Star, Diamond, Sparkles, Heart, BarChart3, Percent,
} from 'lucide-react';
import { useTheme } from '../components/theme-provider';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function Layout() {
  const { theme, setTheme } = useTheme();
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      // Clear any local storage data first
      localStorage.removeItem('straton-video-interactions');
      localStorage.removeItem('straton-theme');
      sessionStorage.removeItem('security-warning-shown');
      
      // Sign out from Supabase
      await signOut();
      
      // Force navigation to login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
      // Even if there's an error, try to redirect to login
      window.location.href = '/login';
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };


  return (
    <div className="min-h-screen bg-background flex overflow-hidden">
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      <aside className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-card border-r border-border flex flex-col transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:static lg:z-0
      `}>
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <div className="absolute -inset-1">
                <div className="w-full h-full mx-auto opacity-30 blur-lg filter bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c] group-hover:opacity-50 transition-opacity duration-500"></div>
              </div>
              <div className="relative bg-card p-2.5 rounded-xl border border-[#bf953f]/20 group-hover:border-[#bf953f]/40 transition-colors duration-500">
                <img 
                  src="https://i.postimg.cc/ZY7X12Zy/Design-sem-nome-5.png" 
                  alt="STRATON Logo" 
                  className="h-12 w-12 object-contain transform group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c]">STRATON</h1>
              <p className="text-sm text-muted-foreground">Olá, Trader</p>
            </div>
            <button 
              onClick={closeSidebar}
              className="lg:hidden text-muted-foreground hover:text-foreground"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-1">
            <button
              onClick={() => { navigate('/dashboard'); closeSidebar(); }}
              className={`flex items-center w-full px-4 py-3 text-base rounded-lg transition-colors ${
                isActive('/dashboard') 
                  ? 'bg-[#bf953f]/10 text-[#bf953f]' 
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <LayoutDashboard className="h-5 w-5 mr-3" />
              Dashboard
            </button>
            <button
              onClick={() => { navigate('/operations'); closeSidebar(); }}
              className={`flex items-center w-full px-4 py-3 text-base rounded-lg transition-colors ${
                isActive('/operations') 
                  ? 'bg-[#bf953f]/10 text-[#bf953f]' 
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <History className="h-5 w-5 mr-3" />
              Operações
            </button>
            <button
              onClick={() => { navigate('/diary'); closeSidebar(); }}
              className={`flex items-center w-full px-4 py-3 text-base rounded-lg transition-colors ${
                isActive('/diary') 
                  ? 'bg-[#bf953f]/10 text-[#bf953f]' 
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <BookOpen className="h-5 w-5 mr-3" />
              Diário do Trader
            </button>
            <button
              onClick={() => { navigate('/paint'); closeSidebar(); }}
              className={`flex items-center w-full px-4 py-3 text-base rounded-lg transition-colors ${
                isActive('/paint') 
                  ? 'bg-[#bf953f]/10 text-[#bf953f]' 
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <Palette className="h-5 w-5 mr-3" />
              Paint Straton
            </button>
            <button
              onClick={() => { navigate('/backtest'); closeSidebar(); }}
              className={`flex items-center w-full px-4 py-3 text-base rounded-lg transition-colors ${
                isActive('/backtest')
                  ? 'bg-[#bf953f]/10 text-[#bf953f]'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <Percent className="h-5 w-5 mr-3" />
              Teste de %
            </button>
            <button
              onClick={() => { navigate('/news'); closeSidebar(); }}
              className={`flex items-center w-full px-4 py-3 text-base rounded-lg transition-colors ${
                isActive('/news') 
                  ? 'bg-[#bf953f]/10 text-[#bf953f]' 
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <Newspaper className="h-5 w-5 mr-3" />
              Notícias
            </button>
            <button
              onClick={() => { navigate('/support'); closeSidebar(); }}
              className={`flex items-center w-full px-4 py-3 text-base rounded-lg transition-colors ${
                isActive('/support') 
                  ? 'bg-[#bf953f]/10 text-[#bf953f]' 
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <Headphones className="h-5 w-5 mr-3" />
              Suporte
            </button>
          </div>

          <div className="mt-6 space-y-2">
            <h3 className="text-base font-medium text-muted-foreground px-4 mb-2">Links Importantes</h3>
            <a
              href="https://instagram.com/straton.trader"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 text-base text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
              onClick={closeSidebar}
            >
              <Instagram className="h-5 w-5 mr-3" />
              Instagram
            </a>
            <a
              href="https://youtube.com/@stratontrader"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 text-base text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
              onClick={closeSidebar}
            >
              <Youtube className="h-5 w-5 mr-3" />
              YouTube
            </a>
            <a
              href="https://chat.whatsapp.com/DQ0QRKtTjHTH9ve6Kwdomc"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-4 py-2 text-base text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
              onClick={closeSidebar}
            >
              <MessageCircle className="h-5 w-5 mr-3" />
              Grupo Grátis
            </a>
          </div>

          <div className="mt-6 space-y-2">
            <h3 className="text-base font-medium text-muted-foreground px-4 mb-2">Área Exclusiva</h3>
            <button
              onClick={() => { navigate('/straton-login'); closeSidebar(); }}
              className={`flex items-center w-full px-4 py-3 text-base rounded-lg transition-all duration-300 group relative overflow-hidden ${
                isActive('/straton-area')
                  ? 'bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c] text-black shadow-lg'
                  : 'text-muted-foreground hover:bg-gradient-to-r hover:from-[#bf953f]/10 hover:via-[#fcf6ba]/10 hover:to-[#aa771c]/10 hover:text-[#bf953f]'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#bf953f]/5 via-[#fcf6ba]/5 to-[#aa771c]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative flex items-center">
                <div className="p-1 rounded-full bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c] mr-3">
                  <Crown className="h-4 w-4 text-black" />
                </div>
                <span className="font-semibold">ÁREA STRATON</span>
              </div>
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-to-r from-[#bf953f] to-[#aa771c] rounded-full animate-pulse"></div>
            </button>
            <button
              onClick={() => { navigate('/straton-tests'); closeSidebar(); }}
              className={`flex items-center w-full px-4 py-3 text-base rounded-lg transition-all duration-300 group relative overflow-hidden ${
                isActive('/straton-tests')
                  ? 'bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c] text-black shadow-lg'
                  : 'text-muted-foreground hover:bg-gradient-to-r hover:from-[#bf953f]/10 hover:via-[#fcf6ba]/10 hover:to-[#aa771c]/10 hover:text-[#bf953f]'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#bf953f]/5 via-[#fcf6ba]/5 to-[#aa771c]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative flex items-center">
                <div className="p-1 rounded-full bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c] mr-3">
                  <BarChart3 className="h-4 w-4 text-black" />
                </div>
                <span className="font-semibold">STRATON TESTES</span>
              </div>
            </button>
          </div>

        </nav>

        <div className="p-4 space-y-2 border-t border-border">
          <button
            onClick={() => {
              const nextTheme = theme === 'dark' ? 'light' : theme === 'light' ? 'pink' : 'dark';
              setTheme(nextTheme);
              closeSidebar();
            }}
            className="flex items-center px-4 py-2 w-full text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
          >
            {theme === 'dark' ? (
              <Sun className="h-5 w-5 mr-3" />
            ) : theme === 'light' ? (
              <Heart className="h-5 w-5 mr-3" />
            ) : (
              <Moon className="h-5 w-5 mr-3" />
            )}
            {theme === 'dark' ? 'Modo Claro' : theme === 'light' ? 'Modo Rosa 💖' : 'Modo Escuro'}
          </button>

          <button
            onClick={() => {
              handleSignOut();
              closeSidebar();
            }}
            className="flex items-center px-4 py-2 w-full text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-h-screen relative overflow-hidden">
        <div className="lg:hidden flex items-center p-4 border-b border-border bg-card">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
          >
            <Menu className="h-6 w-6 text-foreground" />
          </button>
          <div className="ml-4 flex items-center gap-3">
            <img 
              src="https://i.postimg.cc/mDsDsKS4/bbbb.png" 
              alt="STRATON Logo" 
              className="h-10 w-10 object-contain"
            />
            <div>
              <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#bf953f] via-[#fcf6ba] to-[#aa771c]">STRATON</h1>
              <p className="text-sm text-muted-foreground">Olá, Trader</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <Outlet />
        </div>
      </main>

    </div>
  );
}